import {
  AHIcon,
  AHSeparator,
  AHText,
  Box,
  Circle,
  ListItem,
  TouchableBox,
  useLayoutContext,
} from "arkh";
import React, { useCallback, useContext, useMemo, useState } from "react";
import { WATitleInput } from "../../../WidgetAdd/WATitleInput";
import { WTCSStackScreenProps } from "./WTCSStack";

import { ReorderableList } from "../../../../../../components/mobile/ReoardrableListV2/ReordrableList";
import { ReordrableListItem } from "../../../../../../components/mobile/ReoardrableListV2/ReordrableListItem";
import { useIntlTokens } from "../../../../../../components/mobile/useIntlTokens";
import {
  WidgetTaskCollectionContext,
  WidgetTaskCollectionTask,
} from "../WidgetTaskCollectionContext";
import { useIntl } from "react-intl";
import { ListSectionHeader } from "../../../../../../components/atoms/ListSectionHeader";
import Animated from "react-native-reanimated";
import { useFocusEffect } from "@react-navigation/native";

const LIST_ITEM_HEIGHT = 70;

type Props = WTCSStackScreenProps<"WTCSMain">;

export function WTCSMain(props: Props) {
  const { selectedTasks, setSelectedTasks, title, setTitle } = useContext(
    WidgetTaskCollectionContext
  );

  const intl_tokens = useIntlTokens();
  const layout = useLayoutContext();

  const intl = useIntl();
  const numOfTasksInfo = intl.formatMessage(
    {
      defaultMessage: "{numOfTasks} of {maxNumOfTasks} tasks",
      description: "WATaskCollectionSettings max num of tasks info",
    },
    {
      numOfTasks: selectedTasks.length,
      maxNumOfTasks: 8,
    }
  );

  const numOfTasksInfoFull = intl.formatMessage(
    {
      defaultMessage: "{numOfTasks} of {maxNumOfTasks} tasks selected",
      description: "WATaskCollectionSettings max num of tasks info full",
    },
    {
      numOfTasks: selectedTasks.length,
      maxNumOfTasks: 8,
    }
  );

  const onChangeTask = useCallback(() => {
    props.navigation.navigate("WTCSSelectTeams", {});
  }, [props.navigation]);

  const onReorder = useCallback(
    (order: Record<string, number>) => {
      setSelectedTasks((selected) =>
        selected.flatMap((t) => {
          const ordinal = order[keyExtractor(t)];
          if (ordinal !== undefined) {
            return [{ ...t, ordinal }];
          }
          return [];
        })
      );
    },
    [setSelectedTasks]
  );

  const selectedTasksSorted = useMemo(
    () => selectedTasks.slice().sort((a, b) => a.ordinal - b.ordinal),
    [selectedTasks]
  );

  const [k, setK] = useState(0);
  useFocusEffect(
    useCallback(() => {
      setK((f) => f + 1);
    }, [])
  );
  return (
    <Animated.ScrollView
      style={{ flex: 1 }}
      contentContainerStyle={{
        paddingHorizontal: layout.space.medium,
        paddingBottom: layout.space.large,
        paddingTop: layout.space.medium,
      }}
    >
      <Box spaceBottom="large">
        <WATitleInput value={title} onChangeText={setTitle} />
      </Box>
      <ListSectionHeader>
        <AHText type="title-small" style={{ flex: 1 }}>
          {intl_tokens.Tasks}
        </AHText>
        <AHText type="info-small" color="washedText">
          {numOfTasksInfo}
        </AHText>
      </ListSectionHeader>
      <Box style={{ height: LIST_ITEM_HEIGHT * selectedTasksSorted.length }}>
        <ReorderableList
          key={k}
          itemHeight={LIST_ITEM_HEIGHT}
          items={selectedTasksSorted}
          onReorder={onReorder}
          keyExtractor={keyExtractor}
          renderItem={(item: WidgetTaskCollectionTask, r) => (
            <ReordrableListItem
              key={keyExtractor(item)}
              reorderableListItemProps={r}
              right={
                <TouchableBox
                  onPress={() => {
                    setSelectedTasks((tasks) =>
                      tasks.filter(
                        (task) =>
                          !(
                            task.id === item.id &&
                            task.sectionId === item.sectionId
                          )
                      )
                    );
                  }}
                >
                  <Circle color="negative" size="smallMedium">
                    <AHIcon type="minus" color="surface" />
                  </Circle>
                </TouchableBox>
              }
            >
              <ListItem.Title>
                {(item.sectionTitle?.length ?? 0) > 0
                  ? `${item.sectionTitle}: ${item.title}`
                  : `${item.title}`}
              </ListItem.Title>
              {item.routineTitle !== "" && item.routineTitle !== item.title ? (
                <AHText>{item.routineTitle}</AHText>
              ) : null}
            </ReordrableListItem>
          )}
        />
      </Box>
      {selectedTasks.length > 0 ? (
        <AHSeparator spaceVertical="large" spaceHorizontal="small" />
      ) : null}
      {selectedTasks.length === 8 ? (
        <ListItem isFirst isLast>
          <ListItem.Title>{numOfTasksInfoFull}</ListItem.Title>
        </ListItem>
      ) : (
        <ListItem
          isFirst
          isLast
          onPress={onChangeTask}
          left={
            <Circle color="primary" size="smallMedium">
              <AHIcon type="add" color="surface" />
            </Circle>
          }
          right={<ListItem.NavArrow />}
        >
          <ListItem.Title>{intl_tokens.AddTasks}</ListItem.Title>
        </ListItem>
      )}
    </Animated.ScrollView>
  );
}

function keyExtractor(item: { id: string; sectionId: string }) {
  return `${item.id}${item.sectionId}`;
}
